// src/mixins/orderResumeCheckMixin.js
//
// GID-2608 — Order-detail resume / idle change-check.
//
// On the partner order-detail view (Order.vue) the user can return to a stale order
// after the tab/app was backgrounded, or after the view has been open for ~1h, and
// see outdated information. This mixin silently checks whether *that specific order*
// changed in the background when the user comes back and only then acts:
//   - status / substatus moved it to a different bucket -> redirect to the new view
//     (reusing the dormant jobStatusCheckMixin -> /api/partner/jobs-search),
//   - a non-bucket change (price values, comments) -> refresh the detail in place,
//   - nothing changed -> do nothing (no refresh, no redirect, no flicker),
// and never clobbers an in-progress action (dirty form, submit in flight, signing).
//
// Scope: order-detail view only. The sidebar 6-min counts poll (Auth.vue) and the
// list views are deliberately untouched; this per-order request is separate.
import axios from 'axios';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';

// Fallback poll for when the view stays foregrounded (no visibilitychange) for ~1h.
const IDLE_INTERVAL_MS = 60 * 60 * 1000;
// Collapse rapid visibilitychange flicker (e.g. switching windows quickly).
const RESUME_DEBOUNCE_MS = 2000;
// Routes that re-use the Order component. Navigating between buckets keeps the same
// instance alive with an unchanged job_id, so its job_id watcher won't reload — we
// refresh explicitly after such a redirect.
const ORDER_ROUTE_NAMES = ['order', 'order-single', 'order-final-confirmation'];

export default {
  data() {
    return {
      resumeCheck: {
        running: false,
        pending: false,
        lastRunAt: 0,
        idleTimer: null,
      },
    };
  },
  computed: {
    // True once the user has actually edited the open change form.
    // Order.vue auto-opens the next available action (e.g. "Installation beginnen")
    // on load (tryAutoStatusAction) — an auto-opened *pristine* form is NOT mid-action
    // and must not block the check. We cannot key off the per-input `.edit` dirty flag:
    // only DeliveryDate sets it; every other input (professional comment, appointment,
    // address, files, services, custom services) commits without enableEdit, so the
    // flag would miss almost every real edit and the redirect would clobber unsaved
    // work. Instead we snapshot the form payload when the form opens (see the `change`
    // watcher) and treat the form as dirty once the live payload diverges from it.
    resumeCheckHasDirtyInputs() {
      if (!this.change || !this.change.inputs || this.change.loading) {
        return false;
      }
      // Snapshot not captured yet (form just opened) -> still pristine.
      if (this._changeFormBaseline == null) {
        return false;
      }
      return this.resumeFormSignature() !== this._changeFormBaseline;
    },
    // Mid-action guard. Defer the check while the signature flow is active, a submit
    // is in flight (this.change.loading), or the user has edited the open form.
    // These signals are already maintained by Order.vue / the jobs store.
    resumeCheckBlocked() {
      return (
        !!this.signView ||
        !!(this.change && this.change.loading) ||
        this.resumeCheckHasDirtyInputs
      );
    },
  },
  watch: {
    // Run a deferred check once the user finishes / cancels the in-progress action.
    resumeCheckBlocked(blocked) {
      if (!blocked && this.resumeCheck.pending) {
        this.resumeCheck.pending = false;
        this.runResumeCheck('deferred');
      }
    },
    // Snapshot the pristine form payload whenever a change form becomes ready, so
    // resumeCheckHasDirtyInputs can detect a subsequent user edit. initInputs seeds
    // the store synchronously (JOB_INIT_INPUTS) before `this.change` is assigned and
    // input components don't re-commit on mount, so the value read here is the settled
    // pristine payload, not a half-populated one. Clear it when the form closes.
    change(change) {
      if (change && change.inputs && !change.loading) {
        this._changeFormBaseline = this.resumeFormSignature();
      } else if (!change) {
        this._changeFormBaseline = null;
      }
    },
    // The Order component is reused when navigating between orders (same instance,
    // new job_id). Drop any in-flight/pending resume state and the stale form baseline
    // from the previous order so they can't block or mis-fire against the new one;
    // Order.vue's own job_id watcher re-loads and re-captures the order baseline.
    // Re-arm the idle clock.
    job_id() {
      this.resumeCheck.running = false;
      this.resumeCheck.pending = false;
      this._changeFormBaseline = null;
      this.armIdleTimer();
    },
  },
  mounted() {
    this._resumeDestroyed = false;
    this._changeFormBaseline = null;
    this._onResumeVisibility = () => {
      if (document.visibilityState === 'visible') {
        this.runResumeCheck('resume');
      }
    };
    document.addEventListener('visibilitychange', this._onResumeVisibility);
    this.armIdleTimer();
  },
  beforeDestroy() {
    this._resumeDestroyed = true;
    if (this._onResumeVisibility) {
      document.removeEventListener(
        'visibilitychange',
        this._onResumeVisibility,
      );
      this._onResumeVisibility = null;
    }
    this.clearIdleTimer();
  },
  methods: {
    armIdleTimer() {
      this.clearIdleTimer();
      this.resumeCheck.idleTimer = setTimeout(() => {
        this.resumeCheck.idleTimer = null;
        // Keep the fallback ticking regardless of the check's outcome.
        this.armIdleTimer();
        this.runResumeCheck('idle');
      }, IDLE_INTERVAL_MS);
    },
    clearIdleTimer() {
      if (this.resumeCheck.idleTimer) {
        clearTimeout(this.resumeCheck.idleTimer);
        this.resumeCheck.idleTimer = null;
      }
    },
    // Canonical snapshot of everything the open change form would submit: the store
    // inputs (sendInputs == exactly what sendChangeRequest posts; jobInputsAllValues
    // also merges the services/products selections) plus the component-local custom
    // services. Comparing this against the snapshot taken at form-open tells an
    // auto-opened pristine form (safe to refresh/redirect) from one the user edited
    // (must defer), without relying on any per-input dirty flag.
    resumeFormSignature() {
      try {
        return JSON.stringify({
          inputs: this.sendInputs,
          customServices: this.new_custom_services || null,
        });
      } catch (e) {
        return null;
      }
    },
    // Snapshot of the diffed fields, captured after every (re)load so the next
    // resume/idle check compares against what the user currently sees.
    captureChangeBaseline() {
      // Hold the object we create in a local and write the async comment signature
      // back through IT (with an identity check), never through `this._changeBaseline`
      // directly: the Order component is reused across job_id changes, so a still
      // in-flight comment fetch for a previous order must not clobber the current
      // order's baseline.
      const baseline = {
        order: this.buildOrderSignature(this.order),
        comments: null, // filled in asynchronously below
      };
      this._changeBaseline = baseline;
      const apiId = this.order && this.order.api_id;
      if (apiId) {
        this.fetchCommentSignature(apiId)
          .then((sig) => {
            if (this._changeBaseline === baseline) {
              baseline.comments = sig;
            }
          })
          .catch(() => {});
      }
    },
    buildOrderSignature(order) {
      if (!order || !order.job) {
        return { removed: true };
      }
      const job = order.job;
      const substatus =
        job.states && Array.isArray(job.states.list)
          ? job.states.list.slice().sort().join('|')
          : '';
      return {
        removed: false,
        status: job.status != null ? job.status : null,
        substatus,
        lastStatusChange:
          job.last_status_change != null ? job.last_status_change : null,
        price: JSON.stringify({
          lists: job.price_lists != null ? job.price_lists : null,
          purchasing:
            job.price_purchasing != null ? job.price_purchasing : null,
          selling: job.price_selling != null ? job.price_selling : null,
        }),
      };
    },
    buildCommentSignature(comments) {
      if (!Array.isArray(comments)) {
        return null;
      }
      return comments
        .map((c) => `${c.id}:${c.updatedAt || c.updated_at || ''}`)
        .sort()
        .join('|');
    },
    async fetchCommentSignature(jobApiId) {
      const query = RequestQueryBuilder.create({
        filter: {
          field: 'jobApiId',
          operator: CondOperator.EQUALS,
          value: jobApiId,
        },
      }).query();
      const comments = await axios
        .get(`/data-api/comment?${query}`)
        .then(({ data }) => data);
      return this.buildCommentSignature(comments);
    },
    async fetchOrderForCheck() {
      try {
        const { data } = await axios.get(`/api/partner/orders/${this.job_id}`);
        return data;
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          // No longer a partner order (removed / reassigned) -> treat as removed;
          // jobStatusCheckMixin falls through to /offers/<jobId>.
          return false;
        }
        throw error;
      }
    },
    async runResumeCheck(reason) {
      // Mid-action guard — defer; resumed by the resumeCheckBlocked watcher.
      if (this.resumeCheckBlocked) {
        this.resumeCheck.pending = true;
        return;
      }
      // Coalesce overlapping triggers (idle + visibilitychange) and debounce flicker.
      if (this.resumeCheck.running) {
        return;
      }
      const now = Date.now();
      if (
        reason === 'resume' &&
        now - this.resumeCheck.lastRunAt < RESUME_DEBOUNCE_MS
      ) {
        return;
      }
      // Activity happened -> reset the 1h fallback clock.
      this.armIdleTimer();
      // Nothing loaded yet (initial load in flight / failed, or order not found) ->
      // nothing to diff against.
      if (!this.order || !this._changeBaseline) {
        return;
      }

      this.resumeCheck.running = true;
      this.resumeCheck.lastRunAt = now;
      // The order this check is about. The Order component is reused across job_id
      // changes and can be destroyed mid-fetch, so we bail before acting if either
      // happened — otherwise a stale check could navigate the user away from / refresh
      // an order they already left.
      const startJobId = this.job_id;
      try {
        const base = this._changeBaseline;
        const order = await this.fetchOrderForCheck();
        const orderSig = this.buildOrderSignature(order);
        const apiId =
          (order && order.api_id) || (this.order && this.order.api_id);
        let commentSig = base.comments;
        if (apiId) {
          try {
            commentSig = await this.fetchCommentSignature(apiId);
          } catch (e) {
            commentSig = base.comments; // ignore comment-source hiccups
          }
        }

        // Destroyed, navigated to a different order, or the baseline was re-captured
        // (e.g. a loadData(true) after the user's own action) while fetching -> the
        // in-flight check is stale; drop it. The next trigger diffs the fresh baseline.
        if (
          this._resumeDestroyed ||
          this.job_id !== startJobId ||
          this._changeBaseline !== base
        ) {
          return;
        }
        // The user may have started an action while we were fetching — re-check.
        if (this.resumeCheckBlocked) {
          this.resumeCheck.pending = true;
          return;
        }

        const statusMoved =
          orderSig.removed ||
          orderSig.status !== base.order.status ||
          orderSig.substatus !== base.order.substatus ||
          orderSig.lastStatusChange !== base.order.lastStatusChange;
        const priceChanged =
          !orderSig.removed && orderSig.price !== base.order.price;
        const commentsChanged =
          base.comments != null &&
          commentSig != null &&
          commentSig !== base.comments;

        if (statusMoved) {
          this.redirectOrRefreshByBucket(startJobId);
        } else if (priceChanged || commentsChanged) {
          this.loadData(true); // refresh in place; re-captures the baseline
        }
        // else: nothing changed -> no-op (no refresh, no redirect, no flicker)
      } catch (e) {
        // A transient failure must not break the view — try again next trigger.
      } finally {
        this.resumeCheck.running = false;
      }
    },
    redirectOrRefreshByBucket(jobId) {
      const currentPath = this.normalizeResumePath(this.$route.path);
      this.checkJobStatus(jobId, (targetPath) => {
        // checkJobStatus is itself async; the user may have left or switched orders
        // before its callback fires — never act on a stale order.
        if (this._resumeDestroyed || this.job_id !== jobId) {
          return;
        }
        if (this.normalizeResumePath(targetPath) === currentPath) {
          // Same bucket (substatus moved within it) -> refresh in place.
          this.loadData(true);
          return;
        }
        const resolved = this.$router.resolve(targetPath);
        const reusesOrder =
          resolved &&
          resolved.route &&
          ORDER_ROUTE_NAMES.includes(resolved.route.name);
        // Same Order component, different bucket: job_id is unchanged so its watcher
        // won't reload — refresh explicitly once the navigation settles. A redirect to
        // a different view (offer / completed / customer-signature) mounts its own
        // component, which loads its own data. Run on both resolve and reject so a
        // swallowed (redirected/aborted) navigation still refreshes.
        const refreshIfStillHere = () => {
          if (reusesOrder && !this._resumeDestroyed && this.job_id === jobId) {
            this.loadData(true);
          }
        };
        this.$router
          .push(targetPath)
          .then(refreshIfStillHere, refreshIfStillHere);
      });
    },
    normalizeResumePath(path) {
      return (path || '').replace(/\/+$/, '');
    },
  },
};
